import { Box, Typography, CircularProgress } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { log } from '@/logger/logger.ts';
import { AuthContext } from '@/contexts/AuthContext';
import { getAxiosInstance } from '@/services/axiosConfig';
import { SaveIcon } from '@/UI/Icons/SaveIcon';
import { CheckMarkIcon } from '@/UI/Icons/CheckMarkIcon';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import { Input } from '@/UI/Input/Input';
import { Flex, FlexCenHor } from '@/UI/styles';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { XIcon } from '@/UI/Icons/XIcon';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { useWorkflowStore } from '@/state/workflow.state';
import { getModelServiceIconKey } from '@/components/SubflowTemplates/saveModelUtils';
import { useFetchMenuItems } from '@/hooks/useFetchMenuItems';
import { LabelSmRg } from '@/UI/AppTypography/AppTypography';
import type { NodeId } from 'web';
import type { AiModelData, UpdateNodeData } from '@/types/nodes/model';
import type { NodeDefinition } from '@/types/api/nodeDefinition';

const logger = log.getLogger('SaveCustomNode');
const axiosInstance = getAxiosInstance();

interface SaveCustomNodeProps {
  data: AiModelData;
  updateNodeData: UpdateNodeData;
  nodeId: NodeId;
}

export const SaveCustomNode = ({ data, updateNodeData, nodeId }: SaveCustomNodeProps) => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const [nodeName, setNodeName] = useState('');
  const [nodeDescription, setNodeDescription] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { loadUserNodeTypes } = useWorkflowStore();
  const { fetchMenuItems } = useFetchMenuItems();

  const canSaveModel = !data.isImportedModelSaved;

  const handleSaveCustomNode = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleSaveCustomNodeSuccess = useCallback(
    async (nodeName: string) => {
      setDialogOpen(false);
      updateNodeData(nodeId, { isImportedModelSaved: true, name: nodeName });
      await fetchMenuItems();
      await loadUserNodeTypes();
    },
    [fetchMenuItems, loadUserNodeTypes, nodeId, updateNodeData],
  );

  const submitCustomNode = useCallback(async () => {
    if (!data.model) return;
    const requestBody = {
      data: {
        kind:
          data.kind?.type === 'wildcard' && data.kind.model.type === 'imported'
            ? {
                ...data.kind,
                model: {
                  ...data.kind.model,
                  type: 'user_defined', // Convert to the type "user_defined" when saving to the library
                },
              }
            : undefined,
        color: 'Red',
        handles: data.handles,
        name: nodeName,
        description: nodeDescription,
        model: {
          name: data.model.name,
          version: data.model.version,
          service: data.model.service,
        },
        menu: {
          displayName: nodeName,
          icon: 'EmojiObjectsIcon',
          isModel: true,
        },
        schema: data.schema,
        params: data.params,
        version: 2,
      },
      icon: getModelServiceIconKey(data.model.service ?? ''),
      type: 'custommodelV2',
      isModel: true,
      owner: currentUser?.uid,
      dragHandle: '.node-header',
      visibility: 'private',
    };

    try {
      // Sending POST request to the server
      await axiosInstance.post<{ data: NodeDefinition }>(`/v1/user/node-definitions`, requestBody);
      await handleSaveCustomNodeSuccess(nodeName);
    } catch (error) {
      logger.error('Error submitting form:', error);
      // Handle error here (e.g., set an error message)
    }
  }, [data, nodeName, nodeDescription, currentUser?.uid, handleSaveCustomNodeSuccess]);

  const handleDetailsSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setIsSaving(true);
      await submitCustomNode();
      setIsSaving(false);
    },
    [submitCustomNode],
  );

  const handleDialogClose = useCallback((_event?: unknown, reason?: string) => {
    if (reason === 'backdropClick') {
      return;
    }
    setDialogOpen(false);
  }, []);

  const handleNodeNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setNodeName(event.target.value);
  }, []);

  const handleNodDescriptionChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setNodeDescription(event.target.value);
  }, []);

  return (
    <>
      {canSaveModel ? (
        <ButtonContained
          mode="text"
          size="small"
          onClick={handleSaveCustomNode}
          startIcon={<SaveIcon />}
          sx={{ width: 'fit-content' }}
        >
          {t(I18N_KEYS.RECIPE_MAIN.NODES.WILDCARD.SAVE_TO_MY_LIBRARY_CTA)}
        </ButtonContained>
      ) : (
        <FlexCenHor sx={{ gap: 0.5, padding: '6px 4px' }}>
          <CheckMarkIcon />
          <LabelSmRg sx={{ fontWeight: 500, fontFamily: '"DM Sans"' }}>
            {t(I18N_KEYS.RECIPE_MAIN.NODES.WILDCARD.ADDED_TO_MY_LIBRARY)}
          </LabelSmRg>
        </FlexCenHor>
      )}
      <Dialog
        onClose={handleDialogClose}
        open={dialogOpen}
        sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
        disableEscapeKeyDown
      >
        <Box
          onSubmit={(e) => void handleDetailsSubmit(e)}
          component="form"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: { xs: '90%', sm: '420px' },
            border: `1px solid ${color.White08_T}`,
            borderRadius: 2,
            bgcolor: color.Black92,
            p: 3,
            pt: 6,
            gap: 2,
          }}
        >
          <Typography variant="body-lg-sb">
            {t(I18N_KEYS.RECIPE_MAIN.NODES.WILDCARD.SAVE_TO_MY_LIBRARY_DIALOG.TITLE)}
          </Typography>
          <Input
            value={nodeName}
            onChange={handleNodeNameChange}
            required
            label={t(I18N_KEYS.RECIPE_MAIN.NODES.WILDCARD.SAVE_TO_MY_LIBRARY_DIALOG.NAME_LABEL)}
            placeholder={t(I18N_KEYS.RECIPE_MAIN.NODES.WILDCARD.SAVE_TO_MY_LIBRARY_DIALOG.NAME_PLACEHOLDER)}
            sx={{ mt: 2 }}
            size="large"
          />
          <Input
            value={nodeDescription}
            onChange={handleNodDescriptionChange}
            required
            multiline
            rows={3}
            label={t(I18N_KEYS.RECIPE_MAIN.NODES.WILDCARD.SAVE_TO_MY_LIBRARY_DIALOG.DESCRIPTION_LABEL)}
            placeholder={t(I18N_KEYS.RECIPE_MAIN.NODES.WILDCARD.SAVE_TO_MY_LIBRARY_DIALOG.DESCRIPTION_PLACEHOLDER)}
            sx={{ mt: 2 }}
            size="large"
          />
          <Flex
            sx={{
              mt: 2,
              justifyContent: 'flex-end',
              gap: 1,
              width: '100%',
            }}
          >
            <ButtonContained size="small" sx={{ px: 2 }} mode="text" onClick={handleDialogClose}>
              {t(I18N_KEYS.RECIPE_MAIN.NODES.WILDCARD.SAVE_TO_MY_LIBRARY_DIALOG.CTA_CANCEL)}
            </ButtonContained>
            <ButtonContained
              size="small"
              type="submit"
              sx={{ px: 2 }}
              endIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
              disabled={isSaving}
            >
              {t(I18N_KEYS.RECIPE_MAIN.NODES.WILDCARD.SAVE_TO_MY_LIBRARY_DIALOG.CTA_SAVE)}
            </ButtonContained>
          </Flex>
        </Box>
        <AppIconButton onClick={handleDialogClose} sx={{ position: 'absolute', top: 16, right: 16 }}>
          <XIcon />
        </AppIconButton>
      </Dialog>
    </>
  );
};
